/* osyn-ugc-carousel.js — Section UGC (clone Gisou) — v3 -----------------------
 * Couche UGC UNIQUEMENT. Défilement/drag/momentum/snap = moteur GLOBAL osyn-ui.js
 * (`[data-osyn-slider]`) — INTOUCHÉ. Autoplay muet inline = <video-media>.
 *  - BOUCLE INFINIE : clones du set réel des 2 côtés + téléport dans le rAF de scroll.
 *  - DOTS MAISON (realCount). Pas de `data-osyn-dots` → le moteur les ignore.
 *  - v3 : navigation programmatique (dots/flèches/clavier/clic-centrer) routée par
 *    `_scrollToLeft` qui SUSPEND le téléport pendant le smooth-scroll (sinon le téléport
 *    saute en plein vol → flicker + atterrit sur la mauvaise slide → « le titre ne change pas »).
 *    Settle par `scrollend` (pas de timeout fixe), token monotone anti-course, clear sur
 *    tout pointerdown. Dots = instance la plus proche (biais zone réelle).
 *  - CLIC « centrer puis lire » : clic latéral → centre la vidéo (comme Gisou) ;
 *    clic sur la vidéo déjà centrée → plein écran AVEC le son.
 * -------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.customElements && customElements.get('osyn-ugc-carousel')) return;

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var AR = 1.7778;          // ratio carte active 9:16
  var PAD_V = 8;            // padding vertical de la piste (4+4)

  class OsynUGC extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.track    = this.querySelector('[data-osyn-track]');
      this.panelCap = this.querySelector('[data-osyn-ugc-caption]');
      this.panelCta = this.querySelector('[data-osyn-ugc-cta]');
      this.panelTitle = this.querySelector('[data-osyn-ugc-title]');
      this.modal    = this.querySelector('.osyn-ugc__modal');
      this.player   = this.querySelector('[data-osyn-ugc-player]');
      this.mcap     = this.querySelector('[data-osyn-ugc-mcap]');
      this.mcta     = this.querySelector('[data-osyn-ugc-mcta]');
      this.dotsBox  = this.querySelector('[data-osyn-ugc-dots]');
      if (!this.track) return;

      this.realCards = [].slice.call(this.track.querySelectorAll('[data-osyn-ugc-card]'));
      this.realCount = this.realCards.length;
      if (!this.realCount) return;

      this.emphasis = this.style.getPropertyValue('--osyn-ugc-emphasis').trim() !== '0';
      this.loop = this.realCount >= 2;
      this.activeIndex = 0;
      this.modalIndex = -1;
      this._opener = null;
      this._raf = null;
      this._rzT = null;
      this._suspendTeleport = false;
      this._scrollTok = 0;
      this._lastEmph = -1;

      if (this.loop) this._buildLoop();
      this.cards = [].slice.call(this.track.querySelectorAll('[data-osyn-ugc-card]')); // réels + clones
      this._buildDots();
      this._measure();
      this._lockTrackHeight();
      if (this.loop) this._pin(this.realSetWidth);

      // scroll → téléport + carte active + dots
      this._onScroll = function () {
        if (this._raf) return;
        this._raf = requestAnimationFrame(this._syncActive.bind(this));
      }.bind(this);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // tout pointerdown (souris ET tactile) → libère le suspend + invalide le settle en cours
      this._onPointerDown = function () {
        this._suspendTeleport = false;
        this._scrollTok = (this._scrollTok || 0) + 1;
        clearTimeout(this._suspendT);
      }.bind(this);
      this.track.addEventListener('pointerdown', this._onPointerDown, { passive: true });

      // resize / load → re-mesure + re-verrou + re-pin
      this._onResize = this._scheduleResize.bind(this);
      window.addEventListener('resize', this._onResize);
      this._onLoad = this._scheduleResize.bind(this);
      window.addEventListener('load', this._onLoad);

      // clic / clavier carte → « centrer puis lire ». DÉLÉGATION sur le host (bubble) :
      // un vrai clic souris a pour cible la PISTE (le moteur fait `track.setPointerCapture` au
      // pointerdown → le navigateur redirige le `click` vers la piste, pas la carte) ; les listeners
      // par-carte ne se déclencheraient JAMAIS sur un vrai clic. On retrouve la carte via
      // elementFromPoint(souris) ou e.target.closest (tactile/clavier). Bubble sur le host = après
      // la garde capture du moteur → e.defaultPrevented fiable (drag → on saute).
      this.addEventListener('click', function (e) {
        if (e.defaultPrevented) return;
        var card = e.target.closest && e.target.closest('[data-osyn-ugc-card]');
        if (!card) { var ep = document.elementFromPoint(e.clientX, e.clientY); card = ep && ep.closest && ep.closest('[data-osyn-ugc-card]'); }
        if (card && this.contains(card)) this._activate(card);
      }.bind(this), false);
      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var card = e.target.closest && e.target.closest('[data-osyn-ugc-card]');
        if (card && this.contains(card)) { e.preventDefault(); this._activate(card); }
      }.bind(this), false);

      // flèches + clavier du carrousel : routés par _goToReal (suspend) au lieu du moteur (capture → stopImmediatePropagation)
      this.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-osyn-prev],[data-osyn-next]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault(); e.stopImmediatePropagation();
        this._nav(btn.hasAttribute('data-osyn-next') ? 1 : -1);
      }.bind(this), true);
      this.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        if (e.target !== this.track && !this.track.contains(e.target)) return;
        e.preventDefault(); e.stopImmediatePropagation();
        this._nav(e.key === 'ArrowRight' ? 1 : -1);
      }.bind(this), true);

      // contrôles modale
      if (this.modal) {
        this.querySelector('[data-osyn-ugc-close]').addEventListener('click', this.close.bind(this));
        this.querySelector('[data-osyn-ugc-mprev]').addEventListener('click', this._step.bind(this, -1));
        this.querySelector('[data-osyn-ugc-mnext]').addEventListener('click', this._step.bind(this, 1));
        this.modal.addEventListener('click', function (e) { if (e.target === this.modal) this.close(); }.bind(this));
        this.modal.addEventListener('close', this._teardown.bind(this));
      }

      // bouton d'une vidéo sur la fiche : le bouton principal mène l'ajout (états, tiroir, épuisé)
      this.addEventListener('click', function (e) {
        if (!e.target.closest('[data-osyn-ugc-achat]')) return;
        if (this.modal && this.modal.open) this.close();
        var principal = document.querySelector('[data-buy="main"]');
        if (principal) principal.click();
      }.bind(this));

      this._syncActive();
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('scroll', this._onScroll);
        this.track.removeEventListener('pointerdown', this._onPointerDown);
      }
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onLoad);
      if (this._raf) cancelAnimationFrame(this._raf);
      clearTimeout(this._rzT);
      clearTimeout(this._suspendT);
      clearTimeout(this._swapT);
      this._scrollTok = (this._scrollTok || 0) + 1;   // invalide tout settle en vol
    }

    // ---- mesure / boucle ----
    _gap() {
      var cs = getComputedStyle(this.track);
      return parseFloat(cs.columnGap || cs.gap) || 16;
    }
    _measure() {
      var w = this.realCards[0].getBoundingClientRect().width;
      this.cardStride = w + this._gap();
      this.realSetWidth = this.cardStride * this.realCount;
    }
    _buildLoop() {
      var t = this.track;
      if (t.dataset.ugcLooped) return;
      t.dataset.ugcLooped = '1';
      var head = document.createDocumentFragment();
      var tail = document.createDocumentFragment();
      this.realCards.forEach(function (c) {
        head.appendChild(this._clone(c));
        tail.appendChild(this._clone(c));
      }.bind(this));
      t.insertBefore(head, t.firstChild);
      t.appendChild(tail);
    }
    _clone(card) {
      var c = card.cloneNode(true);
      c.dataset.ugcClone = '1';
      c.setAttribute('aria-hidden', 'true');
      c.tabIndex = -1;
      return c;
    }
    _pin(left) {
      var t = this.track, b = t.style.scrollBehavior;
      t.style.scrollBehavior = 'auto';
      t.scrollLeft = left;
      t.style.scrollBehavior = b;
    }
    _offsetOf(card) {
      var trLeft = this.track.getBoundingClientRect().left;
      var padL = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      return card.getBoundingClientRect().left - trLeft - padL;
    }

    // ---- scroll programmatique : SUSPEND le téléport pendant le smooth-scroll ----
    _scrollToLeft(left) {
      var t = this.track, self = this;
      var tok = this._scrollTok = (this._scrollTok || 0) + 1;
      this._suspendTeleport = true;
      clearTimeout(this._suspendT);
      var smooth = !REDUCE.matches;
      t.scrollTo({ left: Math.round(left), behavior: smooth ? 'smooth' : 'auto' });
      var finish = function () {
        if (tok !== self._scrollTok) return;        // un scroll plus récent (ou un pointerdown) prime
        self._suspendTeleport = false;
        // PAS de _maybeTeleport ici : sur écran statique, téléporter réel↔clone ferait sauter la
        // frame vidéo (élements <video> distincts, lectures désynchronisées). Le re-centrage se fait
        // au prochain scroll utilisateur (masqué par le mouvement). La position d'atterrissage reste
        // valide (biais zone réelle → plein runway, jamais de blanc).
      };
      if (!smooth) { finish(); return; }
      this._armSettle(tok, finish);
    }
    _armSettle(tok, done) {
      var t = this.track, self = this;
      if ('onscrollend' in window) {
        var h = function () {
          t.removeEventListener('scrollend', h);
          clearTimeout(self._suspendT);
          done();
        };
        t.addEventListener('scrollend', h, { once: true });
        this._suspendT = setTimeout(function () { t.removeEventListener('scrollend', h); done(); }, 1200); // filet
        return;
      }
      // fallback (pas de scrollend) : poll jusqu'à scrollLeft stable ~3 frames
      var last = t.scrollLeft, still = 0;
      var tick = function () {
        if (tok !== self._scrollTok) return;
        if (Math.abs(t.scrollLeft - last) < 1) { if (++still >= 3) { done(); return; } }
        else still = 0;
        last = t.scrollLeft;
        self._suspendT = setTimeout(function () { requestAnimationFrame(tick); }, 50);
      };
      this._suspendT = setTimeout(function () { requestAnimationFrame(tick); }, 50);
    }
    _maybeTeleport() {
      if (!this.loop || !this.realSetWidth) return;
      if (this.track.style.cursor === 'grabbing' || this._suspendTeleport) return;  // drag moteur OU scroll programmatique
      var x = this.track.scrollLeft, w = this.realSetWidth;
      if (x < w * 0.5) this._jump(w);
      else if (x >= w * 1.5) this._jump(-w);
    }
    _jump(delta) {
      var t = this.track, b = t.style.scrollBehavior;
      t.style.scrollBehavior = 'auto';
      t.scrollLeft += delta;
      t.style.scrollBehavior = b;
    }

    // ---- verrou de hauteur (anti-saut) ----
    _lockTrackHeight() {
      var w = this.realCards[0].getBoundingClientRect().width;
      this.track.style.minHeight = Math.ceil(w * AR + PAD_V) + 'px';
    }

    // ---- dots maison (realCount) ----
    _buildDots() {
      if (!this.dotsBox || this.realCount < 2) return;
      this.dotsBox.classList.add('osyn-pgn');
      var pill = document.createElement('div');
      pill.className = 'osyn-pgn__pill';
      var self = this;
      this.dots = this.realCards.map(function (_, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'osyn-pdot';
        b.setAttribute('aria-label', "Aller à l'élément " + (i + 1) + ' sur ' + self.realCount);
        b.addEventListener('click', function () { self._goToReal(i); });
        pill.appendChild(b);
        return b;
      });
      this.dotsBox.appendChild(pill);
    }
    // dot/nav → instance de la slide i DANS LA BANDE [0.5W,1.5W] la plus proche.
    // Garde la position dans la bande → pas de dérive vers le bord (→ blanc), pas besoin de
    // re-centrer au repos (→ pas de saut de frame vidéo). Scroll court, 0 téléport mid-scroll.
    _goToReal(i) {
      if (!this.loop) { this._scrollToLeft(i * this.cardStride); return; }
      var x = this.track.scrollLeft, w = this.realSetWidth, s = this.cardStride;
      var lo = w * 0.5 - 1, hi = w * 1.5 + 1;
      var cands = [i * s, w + i * s, 2 * w + i * s].filter(function (c) { return c >= lo && c <= hi; });
      if (!cands.length) cands = [w + i * s];            // sécurité (réel toujours dans/au bord de la bande)
      var best = cands[0], bd = Math.abs(best - x);
      cands.forEach(function (c) { var d = Math.abs(c - x); if (d < bd) { bd = d; best = c; } });
      this._scrollToLeft(best);
    }
    _nav(dir) {
      var realIdx = ((this.activeIndex % this.realCount) + this.realCount) % this.realCount;
      this._goToReal((realIdx + dir + this.realCount) % this.realCount);
    }
    // « centrer puis lire » : carte latérale → la centrer ; carte déjà centrée + vidéo → modale son.
    _activate(card) {
      var leading = (card === this.cards[this.activeIndex]) || card.classList.contains('is-active');
      if (!leading) { this._scrollToLeft(this.track.scrollLeft + this._offsetOf(card)); return; }
      if (card.dataset.ugcSrc) this.open(parseInt(card.dataset.index, 10), card);
    }

    // ---- carte active → emphase (throttlée) + sync panneau + dots ----
    _syncActive() {
      this._raf = null;
      this._maybeTeleport();
      var trLeft = this.track.getBoundingClientRect().left;
      var padL = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      var best = 0, bestD = Infinity;
      this.cards.forEach(function (c, i) {
        var d = Math.abs(c.getBoundingClientRect().left - trLeft - padL);
        if (d < bestD) { bestD = d; best = i; }
      });
      if (this.emphasis && best !== this._lastEmph) {       // throttle : ne basculer que quand best change
        this._lastEmph = best;
        this.cards.forEach(function (c, i) { c.classList.toggle('is-active', i === best); });
      }
      var realIdx = ((best % this.realCount) + this.realCount) % this.realCount;
      if (this.dots && this.dots.length) {
        this.dots.forEach(function (d, i) { d.classList.toggle('is-active', i === realIdx); });
      }
      if (best !== this.activeIndex) {
        this.activeIndex = best;
        this._swapPanel(this.cards[best]);
      }
    }

    _swapPanel(card) {
      var txt = card.querySelector('.osyn-ugc__cap-text');
      var cta = card.querySelector('.osyn-ugc__cap-cta');
      var ttl = card.querySelector('.osyn-ugc__cap-title');
      var self = this;
      var apply = function () {
        if (txt && self.panelCap) self.panelCap.innerHTML = txt.innerHTML;
        if (cta && self.panelCta) self.panelCta.innerHTML = cta.innerHTML;
        if (ttl && self.panelTitle) self.panelTitle.textContent = ttl.textContent;
      };
      if (REDUCE.matches) { apply(); return; }
      this.classList.add('is-swapping');
      clearTimeout(this._swapT);
      this._swapT = setTimeout(function () { apply(); self.classList.remove('is-swapping'); }, 160);
    }

    // ---- resize (debounce) ----
    _scheduleResize() {
      clearTimeout(this._rzT);
      this._rzT = setTimeout(this._doResize.bind(this), 150);
    }
    _doResize() {
      this._measure();
      this._lockTrackHeight();
      if (this.loop) {
        var realIdx = ((this.activeIndex % this.realCount) + this.realCount) % this.realCount;
        this._pin(this.realSetWidth + realIdx * this.cardStride);
      }
      this._onScroll();
    }

    // ---- modale (toujours sur le SET RÉEL) ----
    open(index, opener) {
      this.modalIndex = index;
      this._opener = opener || this.realCards[index];
      this._pauseInline();
      this._load(index);
      if (this.modal.showModal) this.modal.showModal(); else this.modal.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }
    _load(index) {
      var card = this.realCards[index];
      var src = card && card.dataset.ugcSrc;
      if (!src) return;
      this.player.src = src;
      this.player.poster = card.dataset.ugcPoster || '';
      this.player.muted = false;
      this.player.loop = false;
      this.player.currentTime = 0;
      var p = this.player.play();                  // SYNCHRONE dans le geste → son autorisé
      if (p && p.catch) p.catch(function () {});
      var txt = card.querySelector('.osyn-ugc__cap-text');
      var cta = card.querySelector('.osyn-ugc__cap-cta');
      if (this.mcap) this.mcap.innerHTML = txt ? txt.innerHTML : '';
      if (this.mcta) this.mcta.innerHTML = cta ? cta.innerHTML : '';
      this.modalIndex = index;
    }
    _step(dir) {
      var n = this.realCount, i = this.modalIndex;
      for (var k = 0; k < n; k++) {
        i = (i + dir + n) % n;
        if (this.realCards[i].dataset.ugcSrc) { this._load(i); return; }
      }
    }
    close() {
      if (this.modal.close) this.modal.close(); else { this.modal.removeAttribute('open'); this._teardown(); }
    }
    _teardown() {
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.load();
      document.documentElement.style.overflow = '';
      this._resumeInline();
      if (this._opener && this._opener.focus) this._opener.focus();
    }
    _pauseInline() {
      this.track.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });
    }
    _resumeInline() {
      if (REDUCE.matches) return;
      var c = this.cards[this.activeIndex];
      var v = c && c.querySelector('video');
      if (v) { try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
    }
  }

  if (window.customElements) customElements.define('osyn-ugc-carousel', OsynUGC);
})();
